import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from "react";
import ComponentTypes from "~/features/cms/componentTypes";
import type { CMSComponentProps } from "~/features/cms/types/props";
import type { CMSTeaserStandardProps, CMSTeaserPublicationProps } from "~/features/cms/types/model";
import {
  CMSComponentRendererProvider,
  initializeCMSComponentRenderer,
  type CMSComponentRenderer,
} from "~/features/cms/CMSComponentRendererContext";

// Homepage-critical CMS types stay statically imported: they render above the
// fold on the storefront home page, where a lazy fetch would flash empty
// content (docs/14 §option 1 risk control). Everything else loads lazily —
// content-driven composition cannot be static imports, and laziness gives
// CMS-component-level code splitting for free (registry-retirement idea).
import { CMSSection } from "~/features/cms/components/cmsSection/CMSSection";
import { CMSButton } from "~/features/cms/components/cmsButton/CMSButton";
import { CMSLink } from "~/features/cms/components/cmsLink/CMSLink";
import { CMSList } from "~/features/cms/components/cmsList/CMSList";
import { CMSStandaloneImage } from "~/features/cms/components/cmsMedia/CMSStandaloneMedia";
import { CMSTeasers } from "~/features/cms/components/cmsTeaser/CMSTeasers";
import CMSTeaser from "~/features/cms/components/cmsTeaser/CMSTeaser";
import { CMSBanner } from "~/features/cms/components/cmsBanner/CMSBanner";
import { CMSRotatingBanners } from "~/features/cms/components/cmsBanner/CMSRotatingBanners";
import { CMSFilterableTileContainer } from "~/features/cms/components/cmsFilterableTile/CMSFilterableTileContainer";
import { CMSParagraph } from "~/features/cms/components/cmsParagraph/CMSParagraph";
import { CMSColumnContainer } from "~/features/cms/components/cmsColumn/CMSColumnContainer";
import { CMSColumn } from "~/features/cms/components/cmsColumn/CMSColumn";
import { CMSColumnLinks } from "~/features/cms/components/cmsColumn/CMSColumnLinks";
import { CMSNewsletterConsent } from "~/features/cms/components/cmsNewsletter/CMSNewsletterConsent";
import { CMSPhoto } from "~/features/cms/components/cmsImage/CMSImage";
import { CMSPhotoGallery } from "~/features/cms/components/cmsImage/CMSPhotoGallery";
import { CMSCardComponent } from "~/features/cms/components/cmsCard/CMSCardComponent";
import { CMSHomepagePersonalization } from "~/features/cms/homepagePersonalization/CMSHomepagePersonalization";
import { CMSChipLink } from "~/features/cms/components/cmsChip/CMSChipLink";
import { CMSChipList } from "~/features/cms/components/cmsChip/CMSChipList";
import { CMSSimpleResponsiveBanner } from "~/features/cms/components/cmsResponsiveBanner/CMSSimpleResponsiveBanner";
import { CMSImageHeader } from "~/features/cms/components/cmsImageHeader/CMSImageHeader";
import { CMSSimpleBanner } from "~/features/cms/components/cmsSimpleBanner/CMSSimpleBanner";
import { CMSRotatingImages } from "~/features/cms/components/cmsRotatingImages/CMSRotatingImages";
import { CMSImageTile } from "~/features/cms/components/cmsImageTile/CMSImageTile";
import { CMSImageTileContent } from "~/features/cms/components/cmsImageTileContent/CMSImageTileContent";
import type { JSX } from "react";

const CMSLoginCall = lazy(() =>
  import("~/features/cms/components/cmsLogin/CMSLoginCall").then((m) => ({ default: m.CMSLoginCall })),
);
const CMSQuote = lazy(() =>
  import("~/features/cms/components/cmsQuote/CMSQuote").then((m) => ({ default: m.CMSQuote })),
);
const CMSAccordionContainer = lazy(() =>
  import("~/features/cms/components/cmsAccordionContainer/CMSAccordionContainer").then((m) => ({
    default: m.CMSAccordionContainer,
  })),
);
const CMSAccordion = lazy(() =>
  import("~/features/cms/components/cmsAccordion/CMSAccordion").then((m) => ({ default: m.CMSAccordion })),
);
const CMSTable = lazy(() =>
  import("~/features/cms/components/cmsTable/CMSTable").then((m) => ({ default: m.CMSTable })),
);
const CMSPageNavigation = lazy(() =>
  import("~/features/cms/components/cmsPageNavigation/CMSPageNavigation").then((m) => ({
    default: m.CMSPageNavigation,
  })),
);
const EwellixPageNavigationComponent = lazy(() =>
  import("~/features/cms/components/cmsPageNavigation/EwellixPageNavigationComponent").then((m) => ({
    default: m.EwellixPageNavigationComponent,
  })),
);
const SchaefflerForm = lazy(() =>
  import("~/features/cms/schaefflerForm/SchaefflerForm").then((m) => ({ default: m.SchaefflerForm })),
);
const SchaefflerFormHiddenField = lazy(() =>
  import("~/features/cms/schaefflerForm/SchaefflerFormHiddenField").then((m) => ({
    default: m.SchaefflerFormHiddenField,
  })),
);
const SchaefflerFormSelectField = lazy(() =>
  import("~/features/cms/schaefflerForm/SchaefflerFormSelectField").then((m) => ({
    default: m.SchaefflerFormSelectField,
  })),
);
const SchaefflerFormTextField = lazy(() =>
  import("~/features/cms/schaefflerForm/SchaefflerFormTextField").then((m) => ({
    default: m.SchaefflerFormTextField,
  })),
);
const SchaefflerFormCheckboxField = lazy(() =>
  import("~/features/cms/schaefflerForm/SchaefflerFormCheckboxField").then((m) => ({
    default: m.SchaefflerFormCheckboxField,
  })),
);
const CMSKameleoonSlot = lazy(() => import("~/features/cms/components/cmsKameleoon/CMSKameleoonSlot"));
const CMSIframe = lazy(() =>
  import("~/features/cms/components/cmsIframe/CMSIframe").then((m) => ({ default: m.CMSIframe })),
);
const CMSProductCarousel = lazy(() =>
  import("~/features/cms/components/cmsProductCarousel/CMSProductCarousel").then((m) => ({
    default: m.CMSProductCarousel,
  })),
);
const CMSSAPRecommendation = lazy(() =>
  import("~/features/cms/components/cmsSapRecommendation/CMSSAPRecommendation").then((m) => ({
    default: m.CMSSAPRecommendation,
  })),
);
const CMSForkPageNavigation = lazy(() =>
  import("~/features/cms/components/cmsForkPageNavigation/CMSForkPageNavigation").then((m) => ({
    default: m.CMSForkPageNavigation,
  })),
);
const CMSCadFileSearch = lazy(() =>
  import("~/features/cms/components/cmsCadFileSearch/CMSCadFileSearch").then((m) => ({
    default: m.CMSCadFileSearch,
  })),
);
const CMSFccRecommendation = lazy(() =>
  import("~/features/cms/components/cmsFccRecommendation/CMSFccRecommendation").then((m) => ({
    default: m.CMSFccRecommendation,
  })),
);
const CMSHeaterSelectionAssistant = lazy(() =>
  import("~/features/cms/components/cmsHeaterSelectionAssistant/CMSHeaterSelectionAssistant").then((m) => ({
    default: m.CMSHeaterSelectionAssistant,
  })),
);

type CMSComponentFor<K extends CMSComponentProps["type"]> = ComponentType<Extract<CMSComponentProps, { type: K }>>;

const COMPONENT_MAP: {
  [K in CMSComponentProps["type"]]: CMSComponentFor<K> | LazyExoticComponent<CMSComponentFor<K>>;
} = {
  [ComponentTypes.CMSSection]: CMSSection,
  [ComponentTypes.CMSStandaloneMedia]: CMSStandaloneImage,
  [ComponentTypes.CMSList]: CMSList,
  [ComponentTypes.CMSButton]: CMSButton,
  [ComponentTypes.CMSLink]: CMSLink,
  [ComponentTypes.CMSTeaserContainer]: CMSTeasers,
  [ComponentTypes.CMSTeaser]: CMSTeaser as ComponentType<CMSTeaserStandardProps>,
  [ComponentTypes.CMSPublicationTeaser]: CMSTeaser as ComponentType<CMSTeaserPublicationProps>,
  [ComponentTypes.CMSBanner]: CMSBanner,
  [ComponentTypes.CMSRotatingBanners]: CMSRotatingBanners,
  [ComponentTypes.CMSFilterableTileContainer]: CMSFilterableTileContainer,
  [ComponentTypes.CMSParagraph]: CMSParagraph,
  [ComponentTypes.CMSLoginCall]: CMSLoginCall,
  [ComponentTypes.CMSQuote]: CMSQuote,
  [ComponentTypes.CMSColumnsContainer]: CMSColumnContainer,
  [ComponentTypes.CMSColumn]: CMSColumn,
  [ComponentTypes.CMSAccordionContainer]: CMSAccordionContainer,
  [ComponentTypes.CMSAccordion]: CMSAccordion,
  [ComponentTypes.CMSTable]: CMSTable,
  [ComponentTypes.CMSPageNavigation]: CMSPageNavigation,
  [ComponentTypes.CMSNewsLetterConsent]: CMSNewsletterConsent,
  [ComponentTypes.CMSPhotoGallery]: CMSPhotoGallery,
  [ComponentTypes.CMSPhoto]: CMSPhoto,
  [ComponentTypes.CMSColumnLinks]: CMSColumnLinks,
  [ComponentTypes.EwellixPageNavigation]: EwellixPageNavigationComponent,
  [ComponentTypes.Form]: SchaefflerForm,
  [ComponentTypes.FormHiddenField]: SchaefflerFormHiddenField,
  [ComponentTypes.FormFieldSelect]: SchaefflerFormSelectField,
  [ComponentTypes.FormFieldText]: SchaefflerFormTextField,
  [ComponentTypes.FormFieldCheckbox]: SchaefflerFormCheckboxField,
  [ComponentTypes.CMSCardComponent]: CMSCardComponent,
  [ComponentTypes.CMSHomepagePersonalization]: CMSHomepagePersonalization,
  [ComponentTypes.CMSChipLink]: CMSChipLink,
  [ComponentTypes.CMSChipList]: CMSChipList,
  [ComponentTypes.CMSKameleoonSlot]: CMSKameleoonSlot,
  [ComponentTypes.CMSIframe]: CMSIframe,
  [ComponentTypes.CMSSimpleResponsiveBanner]: CMSSimpleResponsiveBanner,
  [ComponentTypes.CMSProductCarousel]: CMSProductCarousel,
  [ComponentTypes.CMSSAPRecommendation]: CMSSAPRecommendation,
  [ComponentTypes.CMSImageHeader]: CMSImageHeader,
  [ComponentTypes.CMSSimpleBanner]: CMSSimpleBanner,
  [ComponentTypes.CMSRotatingImages]: CMSRotatingImages,
  [ComponentTypes.CMSImageTile]: CMSImageTile,
  [ComponentTypes.CMSImageTileContent]: CMSImageTileContent,
  [ComponentTypes.CMSForkPageNavigation]: CMSForkPageNavigation,
  [ComponentTypes.CMSCadFileSearch]: CMSCadFileSearch,
  [ComponentTypes.CMSFccRecommendation]: CMSFccRecommendation,
  [ComponentTypes.CMSHeaterSelectionAssistant]: CMSHeaterSelectionAssistant,
} as const;

export const canRenderCMSComponent = (type: string): boolean =>
  Boolean(COMPONENT_MAP[type as CMSComponentProps["type"]]);

const renderCMSComponentInternal: CMSComponentRenderer = (props, className, key): JSX.Element | null => {
  const Component = COMPONENT_MAP[props.type];

  if (!Component) {
    console.error(`Could not find a component for type ${props.type}`);
    return null;
  }

  return (
    <Suspense key={key} fallback={null}>
      {/* @ts-expect-error — prop shapes are enforced per type by COMPONENT_MAP's mapped type */}
      <Component className={className} {...props} />
    </Suspense>
  );
};

initializeCMSComponentRenderer(renderCMSComponentInternal);

export const renderCMSComponent: CMSComponentRenderer = (props, className, key): JSX.Element | null => {
  const component = renderCMSComponentInternal(props, className, key);
  if (!component) {
    return null;
  }
  return <CMSComponentRendererProvider renderer={renderCMSComponentInternal}>{component}</CMSComponentRendererProvider>;
};
